import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Divider,
  Flex,
  Grid,
  Group,
  Menu,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip
} from '@mantine/core';
import {
  IconArrowLeft,
  IconSearch,
  IconEdit,
  IconAlertCircle,
  IconClipboardCheck,
  IconDots,
  IconTrash,
  IconX
  // IconList
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { pathnames } from 'routes/yearlyCalendar';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import Dates from 'components/Dates';
import {
  YearlyCalendarCourse,
  yearlyCalendarSchedulePlanService
} from 'services/yearlyCalendar/schedulePlanService';
import ReadOnlyCalendarScheduleView from './components/ReadOnlyCalendarScheduleView';
import ApprovalModal from './components/ApprovalModal';
import CourseStatusModal from './components/CourseStatusModal';
import { SchedulePlanDetailSkeleton } from './components/SchedulePlanEditSkeleton';

// Helper function to ensure date string has UTC timezone suffix
const ensureUTCFormat = (dateString: string): string => {
  if (!dateString) return dateString;
  // If the date string doesn't end with 'Z' and looks like ISO format, add 'Z'
  if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/) && !dateString.endsWith('Z')) {
    return dateString + 'Z';
  }
  return dateString;
};

const SchedulePlanDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [scheduleViewMode, setScheduleViewMode] = useState<'table' | 'calendar'>('table');
  const [approvalModalOpened, setApprovalModalOpened] = useState(false);

  const [courseStatusModalOpened, setCourseStatusModalOpened] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<YearlyCalendarCourse | null>(null);

  // Fetch schedule plan detail
  const {
    data: schedulePlanData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['yearlyCalendarSchedulePlanDetail', id],
    queryFn: () => yearlyCalendarSchedulePlanService.getSchedulePlanDetail(id!),
    enabled: !!id,
    refetchOnWindowFocus: false
  });

  const schedulePlan = schedulePlanData?.data;
  const totalBudgetValue = useMemo(() => {
    if (!schedulePlan) return 0;
    const apiTotal = (schedulePlan as any).totalBudget;
    if (typeof apiTotal === 'number') return apiTotal as number;
    return (schedulePlan.courses || []).reduce((sum, c) => sum + ((c as any).budget || 0), 0);
  }, [schedulePlan]);

  const handleBack = () => {
    navigate(pathnames.schedulePlan);
  };

  const handleEdit = () => {
    navigate(`${pathnames.schedulePlan}/edit/${id}`);
  };

  const handleOpenApproval = () => {
    setApprovalModalOpened(true);
  };

  const handleOpenCourseStatusModal = (course: YearlyCalendarCourse) => {
    setSelectedCourse(course);
    setCourseStatusModalOpened(true);
  };

  const getStatusBadgeProps = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'planned':
        return { color: 'blue', label: 'Planned' };
      case 'canceled':
        return { color: 'red', label: 'Canceled' };
      case 'dropped':
        return { color: 'orange', label: 'Dropped' };
      default:
        return { color: 'gray', label: 'Unknown' };
    }
  };

  const canUpdateCourseStatus = (course: YearlyCalendarCourse) => {
    return course.statusCourse === 'planned';
  };

  const filteredCourses = useMemo(() => {
    const filtered =
      schedulePlan?.courses.filter(
        (course) =>
          course.courseName.toLowerCase().includes(search.toLowerCase()) ||
          course.courseCode.toLowerCase().includes(search.toLowerCase())
      ) || [];

    return filtered.sort((a, b) => {
      if (!a.planningStart && !b.planningStart) return 0;
      if (!a.planningStart) return 1;
      if (!b.planningStart) return -1;

      return (
        new Date(ensureUTCFormat(a.planningStart)).getTime() -
        new Date(ensureUTCFormat(b.planningStart)).getTime()
      );
    });
  }, [schedulePlan?.courses, search]);

  if (!id) {
    navigate(pathnames.schedulePlan);
    return null;
  }

  if (isLoading) {
    return <SchedulePlanDetailSkeleton />;
  }

  if (error) {
    return (
      <Stack gap='md'>
        <BreadcrumbsComponent
          items={[
            { title: 'Schedule Plan', href: pathnames.schedulePlan },
            { title: 'Detail Schedule Plan', href: '' }
          ]}
        />
        <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red'>
          Failed to load schedule plan details. Please try again later.
        </Alert>
      </Stack>
    );
  }

  if (!schedulePlan) {
    return null;
  }

  return (
    <Stack gap='md'>
      {/* Header */}
      <Group justify='space-between' align='flex-start'>
        <BreadcrumbsComponent
          items={[
            { title: 'Schedule Plan', href: pathnames.schedulePlan },
            { title: 'Detail Schedule Plan', href: '' }
          ]}
        />
        <Group gap='sm'>
          {schedulePlan?.statusApproval.toLocaleUpperCase() === 'PLANNED' && (
            <Button
              leftSection={<IconClipboardCheck size={16} />}
              onClick={handleOpenApproval}
              color='blue'
              variant='light'
            >
              Review & Approve
            </Button>
          )}

          <Button leftSection={<IconEdit size={16} />} onClick={handleEdit}>
            Edit Schedule
          </Button>
        </Group>
      </Group>

      <Grid gutter='lg'>
        <Grid.Col span={8}>
          {/* Planning Overview */}
          <Paper p='md' radius='md' mb='lg' withBorder>
            <Title order={3} mb='md'>
              {schedulePlan.planningName}{' '}
              {schedulePlan.statusApproval.toLocaleUpperCase() === 'PLANNED' ? '(UNPLANNED)' : ''}
            </Title>
            <Badge
              variant='filled'
              color={
                schedulePlan.statusApproval === 'approved'
                  ? 'green'
                  : schedulePlan.statusApproval === 'rejected'
                    ? 'red'
                    : 'yellow'
              }
              size='lg'
            >
              {schedulePlan.statusApproval.toUpperCase() === 'PLANNED'
                ? 'UNPLANNED'
                : schedulePlan.statusApproval || 'Unknown'}
            </Badge>
            <Text c='dimmed' mb='md'>
              {schedulePlan.planningDescription}
            </Text>

            <Group gap='md'>
              <Badge
                variant='light'
                color={schedulePlan.programType === 'modular' ? 'blue' : 'green'}
                size='lg'
              >
                {schedulePlan.programType}
              </Badge>
              <Text size='sm' c='dimmed'>
                {schedulePlan.courses.length} courses • {schedulePlan.targetPositions.length} target
                positions
              </Text>
              {totalBudgetValue > 0 && (
                <Badge variant='outline' color='violet' size='lg'>
                  {new Intl.NumberFormat('id-ID', {
                    style: 'currency',
                    currency: 'IDR',
                    maximumFractionDigits: 0
                  }).format(totalBudgetValue)}
                </Badge>
              )}
            </Group>
          </Paper>

          {/* Courses Table */}
          <Paper p='md' radius='md' withBorder>
            <Group justify='space-between' mb='md'>
              <Title order={4}>Course Schedule</Title>
            </Group>
            <Group justify='space-between' mb='md'>
              <TextInput
                placeholder='Search courses...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftSection={<IconSearch size={16} />}
                style={{ width: 300 }}
              />
              <SegmentedControl
                value={scheduleViewMode}
                onChange={(value) => setScheduleViewMode(value as 'table' | 'calendar')}
                data={[
                  { label: 'Table View', value: 'table' },
                  { label: 'Calendar View', value: 'calendar' }
                ]}
              />
            </Group>

            {scheduleViewMode === 'table' ? (
              <>
                <Box style={{ overflowX: 'auto' }}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ minWidth: 200 }}>Course</Table.Th>
                        <Table.Th style={{ minWidth: 180 }}>Facilitators</Table.Th>
                        <Table.Th style={{ minWidth: 180 }}>Schedule</Table.Th>
                        <Table.Th style={{ minWidth: 80 }}>Duration</Table.Th>
                        <Table.Th style={{ minWidth: 120 }}>Budget</Table.Th>
                        {/* <Table.Th>Module Breakdown</Table.Th> */}
                        <Table.Th style={{ minWidth: 100 }}>CEA Ready</Table.Th>
                        <Table.Th style={{ minWidth: 100 }}>Status</Table.Th>
                        <Table.Th style={{ width: 60 }}>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filteredCourses.map((course, index) => {
                        const statusProps = getStatusBadgeProps(course.statusCourse || '');
                        const canUpdate = canUpdateCourseStatus(course);

                        // Batch indicator: order by date ASC within same msLCMSCourseID
                        const sameCourses = filteredCourses
                          .filter((c) => c.msLCMSCourseID === course.msLCMSCourseID)
                          .sort((a, b) => {
                            if (!a.planningStart && !b.planningStart) return 0;
                            if (!a.planningStart) return 1;
                            if (!b.planningStart) return -1;
                            return (
                              new Date(ensureUTCFormat(a.planningStart)).getTime() -
                              new Date(ensureUTCFormat(b.planningStart)).getTime()
                            );
                          });
                        const batchIndex = sameCourses.findIndex(
                          (c) => c.trLCMSPlanningCoursesID === course.trLCMSPlanningCoursesID
                        );
                        const totalBatches = sameCourses.length;

                        return (
                          <Table.Tr key={`${course.msLCMSCourseID}-${index}`}>
                            <Table.Td>
                              <Stack gap='xs'>
                                <Group gap={6}>
                                  <Text fw={500}>{course.courseName}</Text>
                                  {totalBatches > 1 && (
                                    <Badge size='xs' variant='light' color='gray'>
                                      Batch {batchIndex + 1} of {totalBatches}
                                    </Badge>
                                  )}
                                </Group>
                                <Text size='sm' c='dimmed'>
                                  {course.courseCode}
                                </Text>
                              </Stack>
                            </Table.Td>
                            <Table.Td>
                              {course.facilitators && course.facilitators.length > 0 ? (
                                <Stack gap={6}>
                                  {course.facilitators.map((facilitator, facIndex) => (
                                    <Group key={facIndex} gap={8} wrap='nowrap'>
                                      <Stack gap={0} style={{ flex: 1 }}>
                                        <Group gap={6}>
                                          <Text size='xs' fw={500}>
                                            {facilitator.name}
                                          </Text>
                                          <Tooltip label={facilitator.type} withArrow>
                                            <Badge
                                              size='xs'
                                              variant='light'
                                              color={
                                                facilitator.type === 'PIC Approval'
                                                  ? 'violet'
                                                  : 'teal'
                                              }
                                              style={{ cursor: 'help' }}
                                            >
                                              {facilitator.type === 'PIC Approval' ? 'PIC' : 'SME'}
                                            </Badge>
                                          </Tooltip>
                                        </Group>
                                        <Text size='xs' c='dimmed'>
                                          {facilitator.email}
                                        </Text>
                                      </Stack>
                                    </Group>
                                  ))}
                                </Stack>
                              ) : (
                                <Text size='xs' c='dimmed'>
                                  No facilitator assigned
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {course.planningStart && course.planningEnd ? (
                                <Stack gap='xs'>
                                  <Text size='sm'>
                                    <Dates date={course.planningStart} />
                                  </Text>
                                  <Text size='sm' c='dimmed'>
                                    to <Dates date={course.planningEnd} />
                                  </Text>
                                </Stack>
                              ) : (
                                <Badge variant='outline' color='gray'>
                                  Not Scheduled
                                </Badge>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size='sm'>{course.learningHours}h</Text>
                            </Table.Td>
                            <Table.Td>
                              {'budget' in course && typeof (course as any).budget === 'number' ? (
                                <Tooltip
                                  label={new Intl.NumberFormat('id-ID', {
                                    style: 'currency',
                                    currency: 'IDR',
                                    maximumFractionDigits: 0
                                  }).format((course as any).budget as number)}
                                  position='top'
                                >
                                  <Badge variant='outline' color='violet'>
                                    {new Intl.NumberFormat('id-ID', {
                                      style: 'currency',
                                      currency: 'IDR',
                                      maximumFractionDigits: 0
                                    }).format((course as any).budget as number)}
                                  </Badge>
                                </Tooltip>
                              ) : (
                                <Text size='sm' c='dimmed'>
                                  -
                                </Text>
                              )}
                            </Table.Td>
                            {/* <Table.Td>
                            {course.listModule && course.listModule.length > 0 ? (
                              <Popover width={300} position='bottom' withArrow shadow='md'>
                                <Popover.Target>
                                  <Button
                                    variant='light'
                                    color='blue'
                                    size='xs'
                                    leftSection={<IconList size={14} />}
                                  >
                                    {course.listModule.length} module
                                    {course.listModule.length > 1 ? 's' : ''}
                                  </Button>
                                </Popover.Target>
                                <Popover.Dropdown>
                                  <Stack gap='sm'>
                                    <Text fw={500} size='sm'>
                                      Module Breakdown
                                    </Text>
                                    {course.listModule.map((module, moduleIndex) => (
                                      <Box
                                        key={moduleIndex}
                                        p='xs'
                                        style={{ backgroundColor: '#f8f9fa', borderRadius: '6px' }}
                                      >
                                        <Group justify='space-between' align='flex-start'>
                                          <Badge variant='filled' color='blue' size='sm'>
                                            {module.moduleType}
                                          </Badge>
                                        </Group>
                                        <Text size='xs' c='dimmed' mt={4}>
                                          {new Date(
                                            ensureUTCFormat(module.moduleStart)
                                          ).toLocaleString()}{' '}
                                          -{' '}
                                          {new Date(
                                            ensureUTCFormat(module.moduleEnd)
                                          ).toLocaleString()}
                                        </Text>
                                      </Box>
                                    ))}
                                  </Stack>
                                </Popover.Dropdown>
                              </Popover>
                            ) : (
                              <Text size='sm' c='dimmed'>
                                No modules
                              </Text>
                            )}
                          </Table.Td> */}
                            <Table.Td>
                              {course.isCEAReady !== undefined && course.isCEAReady !== null ? (
                                <Tooltip
                                  label={`CEA is ${course.isCEAReady ? 'ready' : 'not ready'} for processing`}
                                  position='top'
                                >
                                  <Badge
                                    variant='filled'
                                    color={course.isCEAReady ? 'green' : 'red'}
                                    size='sm'
                                  >
                                    {course.isCEAReady ? 'Ready' : 'Not Ready'}
                                  </Badge>
                                </Tooltip>
                              ) : (
                                <Tooltip
                                  label='CEA Ready status has not been set for this course'
                                  position='top'
                                >
                                  <Badge variant='outline' color='gray' size='sm'>
                                    Not Set
                                  </Badge>
                                </Tooltip>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Stack gap='xs'>
                                <Tooltip
                                  label={`Course status: ${statusProps.label}`}
                                  position='top'
                                >
                                  <Badge variant='filled' color={statusProps.color} size='sm'>
                                    {statusProps.label}
                                  </Badge>
                                </Tooltip>
                              </Stack>
                            </Table.Td>
                            <Table.Td>
                              {canUpdate ? (
                                <Menu shadow='md' width={200}>
                                  <Menu.Target>
                                    <ActionIcon variant='subtle' color='gray'>
                                      <IconDots size={16} />
                                    </ActionIcon>
                                  </Menu.Target>

                                  <Menu.Dropdown>
                                    <Menu.Label>Update Status</Menu.Label>
                                    <Menu.Item
                                      leftSection={<IconX size={14} />}
                                      color='red'
                                      onClick={() => handleOpenCourseStatusModal(course)}
                                    >
                                      Cancel Course
                                    </Menu.Item>
                                    <Menu.Item
                                      leftSection={<IconTrash size={14} />}
                                      color='orange'
                                      onClick={() => handleOpenCourseStatusModal(course)}
                                    >
                                      Drop Course
                                    </Menu.Item>
                                  </Menu.Dropdown>
                                </Menu>
                              ) : (
                                <Tooltip label='Status cannot be changed'>
                                  <ActionIcon variant='subtle' color='gray' disabled>
                                    <IconDots size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Box>

                {filteredCourses.length === 0 && (
                  <Center py={40}>
                    <Text c='dimmed'>No courses found</Text>
                  </Center>
                )}
              </>
            ) : (
              <ReadOnlyCalendarScheduleView courses={filteredCourses} />
            )}
          </Paper>

          <Box mt='xl'>
            <Title order={4} mb='md'>
              Notes
            </Title>
            <Card withBorder>
              <Text style={{ whiteSpace: 'pre-line' }}>
                {schedulePlan.notes === '' ? '-' : schedulePlan.notes}
              </Text>
            </Card>
          </Box>
        </Grid.Col>

        {/* Sidebar */}
        <Grid.Col span={4}>
          <Paper p='lg' radius='md' withBorder>
            <Stack>
              <Flex justify={'space-between'} align='center'>
                <Title order={4}>Planning Details</Title>
                <Badge size='md' color='blue'>
                  {schedulePlan.programType}
                </Badge>
              </Flex>
              <Divider />

              <Box>
                <Title order={5} mb='xs'>
                  Timeline
                </Title>
                <Grid>
                  <Grid.Col span={6}>
                    <Text size='sm' color='dimmed'>
                      Schedule Start
                    </Text>
                    <Text fw={500}>
                      <Dates date={schedulePlan.planningStart} fz={14} />
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size='sm' color='dimmed'>
                      Schedule End
                    </Text>
                    <Text fw={500}>
                      <Dates date={schedulePlan.planningEnd} fz={14} />
                    </Text>
                  </Grid.Col>
                </Grid>
              </Box>

              <Box mt='md'>
                <Title order={5} mb='xs'>
                  Summary
                </Title>
                <Group gap='xs'>
                  <Badge variant='outline' size='sm'>
                    {schedulePlan.courses.length} courses
                  </Badge>
                  <Badge variant='outline' size='sm'>
                    {schedulePlan.targetPositions.length} positions
                  </Badge>
                  {totalBudgetValue > 0 && (
                    <Badge variant='outline' size='sm' color='violet'>
                      {new Intl.NumberFormat('id-ID', {
                        style: 'currency',
                        currency: 'IDR',
                        maximumFractionDigits: 0
                      }).format(totalBudgetValue)}
                    </Badge>
                  )}
                </Group>
              </Box>

              <Box mt='md'>
                <Title order={5} mb='xs'>
                  Target Positions
                </Title>
                <Stack gap={4}>
                  {schedulePlan.targetPositions.map((position, index) => (
                    <Text key={position.trLCMSPlanningTargetPositionsID} size='sm' fw={500}>
                      {index + 1}. {position.positionName}
                    </Text>
                  ))}
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Title order={5} mb='xs'>
                  Schedule Status
                </Title>
                {(() => {
                  const scheduledCourses = schedulePlan.courses.filter(
                    (c) => c.planningStart && c.planningEnd
                  ).length;
                  const totalCourses = schedulePlan.courses.length;
                  const percentage = totalCourses > 0 ? (scheduledCourses / totalCourses) * 100 : 0;

                  return (
                    <Stack gap='xs'>
                      <Group justify='space-between'>
                        <Text size='sm'>Progress</Text>
                        <Text size='sm' fw={500}>
                          {scheduledCourses}/{totalCourses} scheduled
                        </Text>
                      </Group>
                      <div
                        style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}
                      >
                        <div
                          style={{
                            width: `${percentage}%`,
                            height: '100%',
                            backgroundColor: percentage === 100 ? '#51cf66' : '#339af0',
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </div>
                      <Badge
                        color={percentage === 100 ? 'green' : percentage > 0 ? 'yellow' : 'gray'}
                        size='sm'
                      >
                        {percentage === 100
                          ? 'Fully Scheduled'
                          : percentage > 0
                            ? 'Partially Scheduled'
                            : 'Unscheduled'}
                      </Badge>
                    </Stack>
                  );
                })()}
              </Box>

              <Divider />

              <Box>
                <Title order={5} mb='xs'>
                  Created by
                </Title>
                <Text fw={500}>{schedulePlan.userIn}</Text>
                <Text size='sm' c='dimmed'>
                  <Dates date={schedulePlan.dateIn} fz={12} />
                </Text>
              </Box>

              {schedulePlan.userUp && schedulePlan.dateUp && (
                <Box>
                  <Title order={5} mb='xs'>
                    Last Updated by
                  </Title>
                  <Text fw={500}>{schedulePlan.userUp}</Text>
                  <Text size='sm' c='dimmed'>
                    <Dates date={schedulePlan.dateUp} fz={12} />
                  </Text>
                </Box>
              )}

              <Divider />

              {/* Action Buttons */}
              <Stack>
                <Button fullWidth leftSection={<IconEdit size={16} />} onClick={handleEdit}>
                  Edit Schedule
                </Button>

                <Button
                  fullWidth
                  variant='subtle'
                  color='gray'
                  onClick={handleBack}
                  leftSection={<IconArrowLeft size={16} />}
                >
                  Go Back
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>
      {schedulePlan && (
        <ApprovalModal
          opened={approvalModalOpened}
          onClose={() => setApprovalModalOpened(false)}
          planningId={schedulePlan.msLCMSPlanningID}
          planningName={schedulePlan.planningName}
          planningDescription={schedulePlan.planningDescription}
        />
      )}

      {selectedCourse && (
        <CourseStatusModal
          opened={courseStatusModalOpened}
          onClose={() => {
            setCourseStatusModalOpened(false);
            setSelectedCourse(null);
          }}
          course={selectedCourse}
          planningId={id!}
        />
      )}
    </Stack>
  );
};

export default SchedulePlanDetailPage;
